import React, { useRef } from 'react';
import { ReportingContainerBaseConfig } from '../../../base';
import {
    DocumentEditorController,
    DocumentEditorLoader,
    DocumentEditorStateProvider,
} from './documentEditorInterface';
import {
    DocumentEditorContainerProps,
    DocumentEditorViewProps,
} from './documentEditorProps';
import {
    Modal,
    ModalContent,
    ModalBody,
    HStack,
    IconButton,
    Icon,
    Text,
    ModalCloseButton,
} from '@chakra-ui/react';
import { AiOutlineClose } from 'react-icons/ai';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    createEditor,
    EditorState,
    EditorSelection,
    EditorInstance,
} from '@varos/editor-core';
import { createTiptapEditor } from '@varos/editor-tiptap';
import { DocumentEditorParameterSchema } from './documentEditorSchema';
import { buildCoreBlocksFromEditorState } from '@varos/research-editor';
import { buildEditorSuggestion } from './documentEditorFactory';
import { generated } from '@varos/research-sdk-api';
import { generateBlockDiff, applyOperationsToContent } from './documentEditorHelper';
import { assert, assertNever } from '@varos/util-typescript';

export function createDocumentEditorContainer(
    config: ReportingContainerBaseConfig,
    loader: DocumentEditorLoader,
    controller: DocumentEditorController,
    View: React.FC<DocumentEditorViewProps>
): React.FC<DocumentEditorContainerProps> {
    const {
        kernel: {
            infra: {
                optionParser: { useOptions },
            },
        },
        context: { useContext },
        infra: {
            editor: { controller: editorController },
        },
        pubsub: { useHandler },
    } = config;

    // TODO limit the scope of the chat state subscription to avoid unnecessary rerenders
    return config.state.editor.subscribe(({ ...containerProps }) => {
        const [searchParams, setSearchParams] = useSearchParams();
        const options = useOptions(DocumentEditorParameterSchema);
        const context = useContext();
        const navigate = useNavigate();

        const mutation = {
            create: config.repository.research.favorite.useCreate(context),
            delete: config.repository.research.favorite.useDelete(context),
            content: {
                update: config.repository.research.document.useUpdate(context),
            },
            suggestion: {
                accept: config.repository.research.suggestion.useAccept(context),
                reject: config.repository.research.suggestion.useReject(context),
            },
        };

        const data = loader.useLoad(context, {
            item: {
                id: options.contentId,
            },
            poll: true,
        });

        const { suggestion } = data;

        // Store editor instance for use in event handlers
        const editorInstanceRef = useRef<EditorInstance | null>(null);

        const state = {
            document: config.state.editor.useState(context),
        };

        const editor = editorController.useProps({
            initial: data.editor.state,
            suggestion: data.editor.suggestion,
            onMount(instance) {
                // Store the editor instance for use in event handlers
                editorInstanceRef.current = instance;
                // console.log('Editor instance mounted and available');
            },
            onUpdate(state) {
                // Don't auto-save when suggestions are present to avoid saving suggested content prematurely
                if (data.editor.suggestion) {
                    console.log('Skipping auto-save due to active suggestion');
                    return;
                }

                // Generate diff between current and previous editor states
                const diff = generateBlockDiff(state, data.editor.state, data.blocks);

                // Only save if there are actual changes
                if (diff.hasChanges) {
                    console.log('Saving block operations:', diff.operations);

                    mutation.content.update
                        .mutateAsync([data.document.id, { blocks: diff.operations }])
                        .catch((error) => {
                            console.error('Failed to save content operations:', error);
                        });
                } else {
                    console.log('No block changes detected, skipping save');
                }
            },
            onSelect(selection) {
                state.document.onChange({
                    ...state.document.value,
                    document: {
                        ...state.document.value.document,
                        selection,
                    },
                });
            },
            onSuggestionUpdate(action) {
                console.log('suggestion updated', action);

                // The editor is telling us about a suggestion state change
                // The backend updates are already handled by the event handlers (approve/reject)
                // This callback is just for informational purposes

                if (action.type === 'accepted') {
                    console.log(
                        'Editor confirmed suggestion was accepted:',
                        action.suggestionId
                    );
                } else if (action.type === 'rejected') {
                    console.log(
                        'Editor confirmed suggestion was rejected:',
                        action.suggestionId
                    );
                } else if (action.type === 'processing') {
                    console.log('Editor is processing suggestion');
                }
            },
        });

        editor.state;

        const handler = useHandler(context, {
            async onEvent(event) {
                if (event.kind === 'open') {
                    console.log('opening document', event.data.content);
                    navigate(`../${event.data.content.id}`);
                }
                if (event.kind === 'approve') {
                    console.log('approving suggestion', event.data.operation);

                    // Use the editor instance to accept the suggestion
                    if (suggestion?.id && editorInstanceRef.current) {
                        // Remove marks from editor
                        editorInstanceRef.current.acceptSuggestion(suggestion.id);

                        // Call backend to accept the suggestion
                        mutation.suggestion.accept
                            .mutateAsync({
                                id: suggestion.id,
                            })
                            .then(() => {
                                console.log('Suggestion accepted in backend');
                            })
                            .catch((error) => {
                                console.error(
                                    'Failed to accept suggestion in backend:',
                                    error
                                );
                            });
                    } else {
                        console.warn(
                            'Cannot accept suggestion: missing suggestion ID or editor instance'
                        );
                    }
                }
                if (event.kind === 'reject') {
                    console.log('rejecting suggestion', event.data.operation);

                    // Use the editor instance to reject the suggestion
                    if (suggestion?.id && editorInstanceRef.current) {
                        // Remove content and marks from editor
                        editorInstanceRef.current.rejectSuggestion(suggestion.id);

                        // Call backend to reject the suggestion
                        mutation.suggestion.reject
                            .mutateAsync({
                                id: suggestion.id,
                            })
                            .then(() => {
                                console.log('Suggestion rejected in backend');
                            })
                            .catch((error) => {
                                console.error(
                                    'Failed to reject suggestion in backend:',
                                    error
                                );
                            });
                    } else {
                        console.warn(
                            'Cannot reject suggestion: missing suggestion ID or editor instance'
                        );
                    }
                }
                if (event.kind === 'replace') {
                    console.log('replacing document', event.data.blocks);
                    const response = await mutation.content.update.mutateAsync([
                        event.data.content.id,
                        { blocks: event.data.blocks, replace: true },
                    ]);
                    console.log('replaced document', response);
                }
                // if (event.kind === 'update') {
                //     if (editorInstanceRef.current) {
                //         console.log('updating document', event.data.operations);

                //         const blocks = event.data.operations.map(
                //             (operation): generated.AnyContentBlockOperation => {
                //                 if (operation.kind === 'create') {
                //                     return operation.block;
                //                 }
                //                 if (operation.kind === 'update') {
                //                     return {
                //                         id: operation.id,
                //                         ...operation.block,
                //                     };
                //                 }
                //                 if (operation.kind === 'delete') {
                //                     // @ts-expect-error
                //                     return {
                //                         ...operation,
                //                         id: operation.id,
                //                         deleted: true,
                //                     };
                //                 }
                //                 assertNever(operation);
                //             }
                //         );

                //         console.log('updating blocks', event.data.content.id, blocks);
                //         await mutation.content.update.mutateAsync([
                //             event.data.content.id,
                //             {
                //                 blocks,
                //             },
                //         ]);
                //     }
                // }
            },
        });

        const props = controller.useProps(context, data, {
            isAnimating: options.action === 'created',
            event: {
                onLoad() {
                    config.pubsub.dispatch({
                        kind: 'current',
                        data: {
                            content: data.document,
                        },
                    });
                },
            },
            editor,
            item: {
                archive: {
                    id: 'archive',
                    name: 'Archive',
                    kind: 'custom',
                    onPerform() {
                        console.log('archive');
                    },
                },
            },
            onOpen(source) {
                searchParams.set('source', source.id);
                setSearchParams(searchParams, { replace: true });
            },
            bookmark: {
                async onAdd() {
                    const response = await mutation.create.mutateAsync({
                        content: data.document.id,
                        note: undefined,
                    });
                    return response;
                },
                async onRemove(favorite) {
                    const response = await mutation.delete.mutateAsync({
                        favoriteId: favorite.id,
                    });
                    return response;
                },
            },
            suggestion: suggestion
                ? {
                      // Suggestion handling is now done through the useHandler event callback
                      // These are stubs for UI compatibility - real work happens in event handlers
                      async onAccept() {
                          console.log(
                              'Suggestion accept triggered (handled by event system)'
                          );
                      },
                      async onReject() {
                          console.log(
                              'Suggestion reject triggered (handled by event system)'
                          );
                      },
                  }
                : undefined,
        });

        return <View {...props} />;
    });
}
